/**
 * The tenant's offices and office groups, from `dim_location`.
 *
 * Replaces `OFFICES` and `OFFICE_GROUPS` in `@mocks/catalog`, which
 * listed six Canarian airports (`LPA-AP`, `TFS-AP`, …) and four groups
 * that were invented for the prototype. canarias.com's real office is
 * `A1`: every picker built on the mock offered codes the tenant does
 * not have and hid the one it does, so a rule scoped from the dropdown
 * could not match a rate shop (Curro, 2026-09-28: "nada mock ya").
 *
 * `OfficeScopePicker` already fetches this endpoint for its own use.
 * This is the same catalogue for the screens that need the raw lists —
 * a select, a label lookup — without duplicating the request.
 */
import { useEffect, useState } from 'react'
import { BACKEND_APIS } from '@constants/routes'

const ENDPOINT = `${BACKEND_APIS.GENERAL_REPRICING_SETUP_API}/office-scope`

//: One request per page load. Price Limits renders a picker per row.
let pending = null
function fetchOnce() {
	if (!pending) {
		pending = fetch(ENDPOINT, { credentials: 'include' })
			.then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
			.then(body => body?.items || [])
		pending.catch(() => { pending = null })
	}
	return pending
}

const EMPTY = { loading: true, offices: [], groups: [], byCode: new Map(), error: null }

export function useOfficeScope() {
	const [state, setState] = useState(EMPTY)

	useEffect(() => {
		let alive = true
		fetchOnce()
			.then(items => {
				if (!alive) return
				setState({
					loading: false, error: null,
					offices: items.filter(i => i.kind !== 'group'),
					groups:  items.filter(i => i.kind === 'group'),
					byCode:  new Map(items.map(i => [i.code, i])),
				})
			})
			.catch(e => {
				if (!alive) return
				setState({ ...EMPTY, loading: false,
				           error: e?.message || 'Could not load the office catalogue' })
			})
		return () => { alive = false }
	}, [])

	return state
}
